// Hook + fetcher for the firm-dashboard AI activity panel.
//
// Backend gap (2026-04-26): `aiAssistantRouter` has no firm-wide AI
// activity feed (no /api/ai/firm-stream, no /api/ai/recent). The closest
// list-shaped endpoint is GET /api/ai/conversations — the current user's
// recent Ask DuraLex threads. We surface those rather than fabricate a
// "stream" of synthetic events. When the backend grows a real firm-wide
// activity feed (extracts/matches/flags across all matters), swap the
// fetcher and the AIStream component will continue to render without
// further changes.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { AiConversationsResponse, AiConversationSummary } from "@duralex/shared";
import { getAuthHeaders } from "./auth";

export async function fetchAiConversations(): Promise<AiConversationsResponse> {
  const res = await fetch("/api/ai/conversations", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`fetchAiConversations: ${res.status}`);
  return (await res.json()) as AiConversationsResponse;
}

export interface UseAIStreamOpts {
  /** Cap the number of items returned to the caller. Default: 10. */
  limit?: number;
  /** Re-fetch interval in ms. Default: 30s. Set to 0/false to disable. */
  refetchInterval?: number | false;
}

export interface UseAIStreamResult {
  items: AiConversationSummary[];
  isLoading: boolean;
  isError: boolean;
  refetch: UseQueryResult<AiConversationsResponse>["refetch"];
}

/**
 * Recent firm AI assistant activity. Returns the caller's own recent
 * conversations until the backend exposes a firm-wide feed. Re-fetches
 * on a 30-second cadence by default.
 */
export function useAIStream(opts: UseAIStreamOpts = {}): UseAIStreamResult {
  const limit = opts.limit ?? 10;
  const refetchInterval = opts.refetchInterval === undefined ? 30_000 : opts.refetchInterval;

  const query = useQuery({
    queryKey: ["ai", "conversations"],
    queryFn: fetchAiConversations,
    staleTime: 15_000,
    refetchInterval,
  });

  const items = (query.data?.conversations ?? []).slice(0, limit);
  return {
    items,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
